/**
 * "Look here first" pictures for Can't find it (Regina, 10/28).
 *
 * Before a tech can say a device isn't there, the release dialog shows him
 * where it was: the last known location, its pin, and pictures from earlier
 * visits. The pictures come from the job's Site History (the same set the
 * test screen's "All photos" shows), filtered to this device, newest first.
 */
import { attachmentDownloadUrl } from "@/lib/api";

export interface SiteHistoryItem {
    id: string;
    kind: "photo" | "document";
    name: string;
    date: string;
    assetId: string | null;
}

export interface SiteHistoryResponse {
    siteId: string | null;
    groups: { assetId: string | null; label: string; items: SiteHistoryItem[] }[];
}

export interface PriorPhoto {
    src: string;
    caption: string;
}

function captionDate(iso: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "Earlier visit";
    return d.toLocaleDateString(undefined, { month: "short", year: "numeric" });
}

/** This device's pictures from earlier visits, newest first (at most `limit`). */
export function priorPhotosForDevice(
    history: SiteHistoryResponse | undefined,
    assetId: string,
    limit = 6,
): PriorPhoto[] {
    const items: SiteHistoryItem[] = [];
    for (const group of history?.groups ?? []) {
        if (group.assetId !== assetId) continue;
        for (const item of group.items) if (item.kind === "photo") items.push(item);
    }
    items.sort((a, b) => String(b.date).localeCompare(String(a.date)));
    return items.slice(0, limit).map((item) => ({
        src: attachmentDownloadUrl(item.id, { disposition: "inline" }),
        caption: captionDate(item.date),
    }));
}

/** Plain words for a release, the same in the field and the office (10/28). */
export const RELEASE_LABELS: Record<"not_located" | "could_not_test", string> = {
    not_located: "Can't find it",
    could_not_test: "Couldn't get to it",
};

/** Plain words for a release reason chip. */
export function releaseCategoryLabel(category: string | null | undefined): string | null {
    switch (category) {
        case "no_access":
            return "Locked / no key";
        case "manager_unavailable":
            return "Nobody to let us in";
        case "unsafe":
            return "Unsafe";
        case "blocked":
            return "Blocked or buried";
        case "access_other":
            return "Other access problem";
        case "water_shutoff":
            return "Water can't be shut off";
        case "no_water":
            return "No water to device";
        case "ran_out_of_time":
            return "Out of time";
        case "device_gone":
            return "Tech thinks it was removed";
        case "not_found":
            return "Not where the records say";
        default:
            return null;
    }
}

/**
 * "Can't find it" records a FAIL (Corbin, 10/29): "according to several
 * cities an unable to locate is the same as a failed report." The server
 * saves it with this one reason (src/server/services/testState.ts, a test
 * holds the two in step).
 */
export const UNABLE_TO_LOCATE_REASON = "Unable to locate assembly";

/** The ⓘ line on Can't find it. */
export const CANT_FIND_IT_IS_A_FAIL =
    "Can't find it records a FAIL — most cities count unable-to-locate as a failed test.";

/** A saved test row that is an unable-to-locate FAIL. */
export function isUnableToLocateResult(
    row: { overall_result?: string | null; failure_reasons?: readonly string[] | null } | null | undefined,
): boolean {
    if (!row || String(row.overall_result ?? "").toLowerCase() !== "fail") return false;
    return (row.failure_reasons ?? []).includes(UNABLE_TO_LOCATE_REASON);
}
